import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Input,
  Modal,
  PinInput,
  ScrollArea,
  Stack,
  Text,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { WarningCircleIcon } from "@phosphor-icons/react"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import {
  ICustodyExpectedItem,
  ICustodyReleasedItem,
  ICustodyTransfer,
} from "../../apis/custody-transfer"
import { DeliveryActionButton } from "../delivery/action-controls"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { useCustodyMutations } from "./hooks"

interface ItemRow {
  sku: string | undefined
  productId: string | undefined
  name: string | undefined
  unit: string | undefined
  expected: number
  checked: boolean
}

interface FormValues {
  pin: string
  items: ItemRow[]
}

interface Props {
  opened: boolean
  onClose: () => void
  transferId: string
  transfer: ICustodyTransfer | null | undefined
}

const buildRows = (expectedItems: ICustodyExpectedItem[] | null | undefined): ItemRow[] =>
  (expectedItems ?? []).map((it) => ({
    sku: it.sku,
    productId: it.productId,
    name: it.name,
    unit: it.unit,
    expected: it.quantity,
    checked: true,
  }))

// Map backend custody error keys to friendly Mongolian copy so a wrong PIN /
// wrong party reads clearly instead of dumping the raw "error.custody.*" key.
const friendlyError = (msg: string | undefined, t: TFunction): string => {
  if (!msg) return t("custody.error")
  if (msg.includes("bad_pin")) return t("custody.verify.badPin")
  if (msg.includes("pin.locked")) return t("custody.verify.pinLocked")
  if (msg.includes("pin.not_configured")) return t("custody.verify.pinNotConfigured")
  if (msg.includes("not_sender")) return t("custody.detail.onlySenderCanVerify")
  return translateBackendError(msg)
}

export function SenderVerifyDialog({ opened, onClose, transferId, transfer }: Props) {
  const { t } = useTranslation("delivery")
  const { senderVerify, submitting, error } = useCustodyMutations()
  const { stateOf } = useDeliveryActions()

  const form = useForm<FormValues>({
    initialValues: {
      pin: "",
      items: buildRows(transfer?.expectedItems),
    },
    validate: {
      pin: (v) => (/^\d{6}$/.test(v) ? null : t("custody.verify.pinRequired")),
    },
  })

  // Re-seed items whenever the dialog re-opens with a different transfer.
  useEffect(() => {
    if (opened) {
      form.setValues({
        pin: "",
        items: buildRows(transfer?.expectedItems),
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, transfer?.id])

  const handleSubmit = async (values: FormValues) => {
    try {
      // Only send releasedItems when there are expected lines — empty handovers
      // (internal transfers w/o item lists) stay PIN-only on the BE. Only the
      // checked lines are released; unchecked items are flagged short on the BE.
      const releasedItems: ICustodyReleasedItem[] | undefined =
        values.items.length > 0
          ? values.items
              .filter((row) => row.checked)
              .map((row) => ({
                sku: row.sku,
                productId: row.productId,
                name: row.name,
                unit: row.unit,
                quantity: row.expected,
                condition: "GOOD",
              }))
          : undefined
      await senderVerify(transferId, { pin: values.pin, releasedItems })
      form.reset()
      onClose()
    } catch {
      // error shown via mutations hook
    }
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  const items = form.values.items
  const checkedCount = items.filter((r) => r.checked).length
  const allChecked = items.length > 0 && checkedCount === items.length
  const someChecked = checkedCount > 0 && !allChecked
  const hasUnchecked = items.length > 0 && checkedCount < items.length
  const hasTable = items.length > 0

  const toggleAll = (next: boolean) =>
    form.setFieldValue("items", items.map((r) => ({ ...r, checked: next })))

  // O08: ORD_DN_MNG + caller = fromUser (BE transfer-sender-verify.ts). Shown, never hidden.
  const verifyState = stateOf("senderVerify", transfer)

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Box>
          <Text fw={650} fz="var(--nw-fz-drawer)">
            {t("custody.verify.senderTitle")}
          </Text>
          <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
            {[transfer?.deliveryNote?.refCode, transfer?.route?.routeNumber, t("manage.handover.subtitleSuffix")]
              .filter(Boolean)
              .join(" · ")}
          </Text>
        </Box>
      }
      size={440}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {hasTable ? (
            <Box>
              <Group justify="space-between" mb="xs" wrap="nowrap">
                <Checkbox
                  size="sm"
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={(e) => toggleAll(e.currentTarget.checked)}
                  label={
                    <Text size="sm" fw={500}>
                      {t("custody.verify.releasingItems")}{" "}
                      <Text span c="dimmed" fw={400}>
                        ({checkedCount}/{items.length})
                      </Text>
                    </Text>
                  }
                />
                {hasUnchecked && (
                  <Badge color="gray" variant="light" leftSection={<WarningCircleIcon size={12} />}>
                    {t("custody.verify.short")}
                  </Badge>
                )}
              </Group>

              <ScrollArea.Autosize mah={300} type="auto">
                <Stack gap={6}>
                  {items.map((row, idx) => (
                    <Group
                      key={`${row.sku ?? row.productId ?? "row"}-${idx}`}
                      wrap="nowrap"
                      align="flex-start"
                      gap="sm"
                      p="xs"
                      bg={row.checked ? "gray.0" : undefined}
                      bd="1px solid var(--nw-table-border)"
                      bdrs="sm"
                    >
                      <Checkbox
                        mt={2}
                        size="sm"
                        color="dark"
                        checked={row.checked}
                        onChange={(e) =>
                          form.setFieldValue(`items.${idx}.checked`, e.currentTarget.checked)
                        }
                      />
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" fw={500} style={{ wordBreak: "break-word" }}>
                          {row.name ?? row.sku ?? row.productId ?? "—"}
                        </Text>
                        {(row.sku || row.productId) && (
                          <Text size="sm" c="dimmed" ff="monospace" style={{ wordBreak: "break-all" }}>
                            {row.sku ?? row.productId}
                          </Text>
                        )}
                      </Box>
                      <Text size="sm" fw={600} style={{ whiteSpace: "nowrap" }}>
                        {row.expected}
                        {row.unit ? ` ${row.unit}` : ""}
                      </Text>
                    </Group>
                  ))}
                </Stack>
              </ScrollArea.Autosize>
            </Box>
          ) : (
            <Alert color="gray" variant="light" icon={<WarningCircleIcon size={18} />}>
              {t("custody.verify.noItemsPinOnly")}
            </Alert>
          )}

          <Input.Wrapper
            label={t("custody.verify.senderPinLabel")}
            required
            description={t("custody.verify.pinHint")}
            inputWrapperOrder={["label", "input", "description", "error"]}
            error={form.errors.pin}
          >
            <Group justify="flex-start" my={4}>
              <PinInput
                length={6}
                type="number"
                inputMode="numeric"
                size="lg"
                mask
                oneTimeCode
                aria-label={t("custody.pinLabel")}
                value={form.values.pin}
                onChange={(v) => form.setFieldValue("pin", v)}
                error={!!form.errors.pin}
                autoFocus
              />
            </Group>
          </Input.Wrapper>

          {error && (
            <Alert color="red" variant="light" icon={<WarningCircleIcon size={18} />}>
              {friendlyError(error.message, t)}
            </Alert>
          )}

          <Stack gap={4} align="flex-end" mt="sm">
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={handleClose} disabled={submitting}>
                {t("custody.cancel")}
              </Button>
              <DeliveryActionButton state={verifyState} type="submit" loading={submitting} color="dark">
                {t("custody.card.confirm")}
              </DeliveryActionButton>
            </Group>
            {!verifyState.allowed && (
              <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                {verifyState.reason}
              </Text>
            )}
          </Stack>
        </Stack>
      </form>
    </Modal>
  )
}
